import { useEffect, useState } from "react";
import { GradingData } from "../../../views/GradingCenterView";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import { Alert, StyleSheet, View } from "react-native";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomAccordion from "../../../components/common/general/CustomAccordion/CustomAccordion";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";

import { Star } from "@phosphor-icons/react";
import { DEFAULT_QUESTIONS_COUNT } from "../../../views/CustomCourseAssessmentStudentView";
import { ActivityIndicator } from "react-native";
import React from "react";

interface StudentResponse {
  name: string;
  attempt: number;
  dateTaken: string;
  totalScore: number;
  timeSpent: number;
  questions: {
    id: number;
    question: string;
    answer: string;
    score: number;
    maxScore: number;
  }[];
}

const AssessmentGradingModalContainer = ({
  isVisible,
  onClose,
  gradingData,
  onSave,
  isSubmitting,
  isLoading,
}: {
  isVisible: boolean;
  onClose: () => void;
  gradingData: GradingData | null;
  onSave: (gradingData: GradingData, newScore: number) => Promise<void>;
  isSubmitting: boolean;
  isLoading: boolean;
}) => {
  const [grade, setGrade] = useState<string>("");

  const numberOfAnsweredQuestions =
    gradingData?.questions?.filter((question) => question?.userAnswer?.[0]?.answer).length ?? DEFAULT_QUESTIONS_COUNT;

  const handleGradeChange = (text: string) => {
    const newScore = Number(text);

    if (Number.isNaN(newScore)) {
      Alert.alert("Invalid score", "Please enter a valid number");
      return;
    }

    if (newScore < 0 || newScore > numberOfAnsweredQuestions * 10) {
      Alert.alert("Invalid score", "Please enter a score between 0 and 100");
      return;
    }

    setGrade(newScore.toString());
  };

  useEffect(() => {
    if (gradingData?.totalPoints) {
      setGrade(gradingData.totalPoints.toString());
    }
  }, [gradingData]);

  const handleSave = async () => {
    const newScore = Number(grade);

    if (newScore < 0 || newScore > numberOfAnsweredQuestions * 10) {
      Alert.alert("Invalid score", "Please enter a score between 0 and 100");
      return;
    }

    if (Number.isNaN(newScore)) {
      Alert.alert("Invalid score", "Please enter a valid number");
      return;
    }

    await onSave(gradingData, newScore);
  };

  const calculateTimeSpent = () => {
    const startedAt = new Date(gradingData?.startedAt || "");
    const completedAt = new Date(gradingData?.completedAt || "");
    const timeSpent = completedAt.getTime() - startedAt.getTime();
    const minutes = Math.floor(timeSpent / (1000 * 60));

    if (Number.isNaN(minutes)) {
      return "not available";
    }

    return `${minutes} min`;
  };

  return (
    <CustomModal
      visible={isVisible}
      onRequestClose={onClose}
      title="Assessment Responses"
      showCloseButton
      style={styles.modalContent}
      exitFill="#6B7280"
      onShow={() => {}}
      ariaLabel="Assessment Responses Modal">
      <CustomText
        text="Review the student's responses, how they were graded, and any manual adjustments made."
        size="m"
        style={{ color: "#6B7280", marginBottom: 24 }}
        useTranslationText={false}
      />
      {isLoading ? (
        <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
          <ActivityIndicator size="large" color="#0000ff" />
        </View>
      ) : (
        <>
          <View style={styles.studentInfo}>
            <View style={styles.infoItem}>
              <CustomText text="Name:" size="s" weight="bold" useTranslationText={false} />
              <CustomText text={gradingData?.userName || ""} size="s" useTranslationText={false} />
            </View>
            <View style={styles.infoItem}>
              <CustomText text="Date Taken:" size="s" weight="bold" useTranslationText={false} />
              <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
                <CustomText
                  text={new Date(gradingData?.questions?.[0]?.userAnswer?.[0]?.createdAt || "").toLocaleDateString()}
                  size="s"
                  useTranslationText={false}
                />
                <CustomText text="at" size="s" useTranslationText={false} />
                <CustomText
                  text={new Date(gradingData?.questions?.[0]?.userAnswer?.[0]?.createdAt || "")
                    .toLocaleTimeString()
                    .slice(0, 4)}
                  size="s"
                  useTranslationText={false}
                />
              </View>
            </View>
            <View style={styles.infoItem}>
              <CustomText text="Total Score:" size="s" weight="bold" useTranslationText={false} />
              <CustomTextInput
                value={grade}
                onChangeText={handleGradeChange}
                placeholder="Enter total score"
                style={{
                  backgroundColor: "white",
                  borderRadius: 8,
                  paddingHorizontal: 6,
                  paddingVertical: 4,
                  width: 60,
                }}
                keyboardType="numeric"
              />
              <CustomText text={`/${DEFAULT_QUESTIONS_COUNT * 10}`} size="s" useTranslationText={false} />
            </View>
            <View style={styles.infoItem}>
              <CustomText text="Time Spent:" size="s" weight="bold" useTranslationText={false} />
              <CustomText text={calculateTimeSpent()} size="s" useTranslationText={false} />
            </View>
          </View>

          <View style={styles.questionsContainer}>
            {gradingData?.questions.map((question, index) => (
              <View
                key={index}
                style={{ paddingLeft: 16, paddingRight: 32, borderWidth: 1, borderColor: "#EAECF0", borderRadius: 16 }}>
                <CustomAccordion
                  isExpanded={false}
                  title={
                    <View style={styles.accordionHeader}>
                      <CustomText
                        text={`Question ${index + 1}: ${question.question}`}
                        size="s"
                        useTranslationText={false}
                        style={styles.questionText}
                      />
                      <View style={styles.accordionActions}>
                        <View style={styles.scorePill}>
                          <CustomText
                            text={`${question.userAnswer?.[0]?.totalPoints ?? 0}/${question.totalScore ?? "-"}`}
                            size="s"
                            useTranslationText={false}
                          />
                        </View>
                      </View>
                    </View>
                  }>
                  <View style={styles.answerSection}>
                    <CustomText text="Student Response:" size="s" weight="bold" useTranslationText={false} />
                    <CustomText text={question.userAnswer?.[0]?.answer || ""} size="s" useTranslationText={false} />
                  </View>
                  <View style={styles.answerSection}>
                    <CustomText text="Grading Criteria:" size="s" weight="bold" useTranslationText={false} />
                    {question.rubrics?.map((rubric, index) => {
                      const numStars = Math.min(Math.round((rubric.pointsAwarded / rubric.maxPoints) * 4), 4);
                      return (
                        <View key={index} style={{ paddingVertical: 4, gap: 4, paddingHorizontal: 0 }}>
                          <CustomAccordion
                            isExpanded={false}
                            title={
                              <View
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  gap: 4,
                                  padding: 0,
                                }}>
                                <CustomText
                                  text={rubric.component || ""}
                                  size="s"
                                  useTranslationText={false}
                                  style={{ color: "#101828", fontWeight: "bold" }}
                                />
                                <CustomText text=" -> " size="s" useTranslationText={false} />
                                {Array.from({ length: numStars }).map((_, index) => (
                                  <Star size={16} weight="fill" color="#FFD700" key={index} />
                                ))}
                                <CustomText
                                  text={`(${rubric.pointsAwarded}/${rubric.maxPoints} points)`}
                                  size="s"
                                  useTranslationText={false}
                                />
                              </View>
                            }>
                            <CustomText text={rubric.remarks || ""} size="s" useTranslationText={false} />
                          </CustomAccordion>
                        </View>
                      );
                    })}
                  </View>
                </CustomAccordion>
              </View>
            ))}
          </View>

          <View style={styles.modalFooter}>
            <View style={styles.footerButtons}>
              <CustomButton text="Cancel" styleType="secondary" onPress={onClose} useTranslationText={false} />
              <CustomButton
                text="Save"
                styleType="primary"
                onPress={handleSave}
                useTranslationText={false}
                disabled={isSubmitting}
              />
            </View>
          </View>
        </>
      )}
    </CustomModal>
  );
};

export default AssessmentGradingModalContainer;

const styles = StyleSheet.create({
  modalContent: {
    maxHeight: "90%",
  },
  studentInfo: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginBottom: 24,
    gap: 12,
  },
  infoItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  scorePill: {
    backgroundColor: "#E6F4EA",
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 16,
  },
  questionsContainer: {
    gap: 16,
    width: "100%",
    flex: 1,
    overflow: "scroll",
  },
  accordionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  questionText: {
    flex: 1,
    paddingRight: 16,
  },
  accordionActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  answerSection: {
    gap: 8,
    paddingVertical: 4,
    paddingHorizontal: 16,
  },
  modalFooter: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 16,
    marginTop: 24,
    paddingTop: 16,
  },
  footerButtons: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 16,
  },
});
